import { useMemo, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Button, EmptyState, Modal, Select, toast } from '@/components/ui'
import {
  useAddSampleRequestSubscriber,
  useDeleteSampleRequestSubscriber,
  useSampleRequestSubscribers,
} from '../api/queries'
import type { SampleRequestEvent, SampleRequestSubscriber } from '../api/types'

const EVENT_LABEL: Record<SampleRequestEvent, string> = {
  sample_request_created: 'New Request Created',
  sample_request_shipped: 'Sample Shipped',
  sample_request_delivered: 'Sample Delivered',
  sample_request_overdue: 'Overdue to Ship (daily nudge)',
}

const DEFAULT_EVENTS: SampleRequestEvent[] = [
  'sample_request_created',
  'sample_request_shipped',
  'sample_request_delivered',
  'sample_request_overdue',
]

export function SampleRequestNotificationsSection() {
  const { data, isLoading } = useSampleRequestSubscribers()
  const add = useAddSampleRequestSubscriber()
  const remove = useDeleteSampleRequestSubscriber()

  const [pending, setPending] = useState<SampleRequestSubscriber | null>(null)
  const [addOpen, setAddOpen] = useState(false)

  const events: SampleRequestEvent[] = useMemo(
    () => (data?.events && data.events.length > 0 ? data.events : DEFAULT_EVENTS),
    [data],
  )

  const grouped = useMemo(() => {
    const map = new Map<SampleRequestEvent, SampleRequestSubscriber[]>()
    for (const e of events) map.set(e, [])
    for (const s of data?.subscribers ?? []) {
      const list = map.get(s.event) ?? []
      list.push(s)
      map.set(s.event, list)
    }
    return map
  }, [data, events])

  function confirmRemove() {
    if (!pending) return
    const p = pending
    remove.mutate(p.id, {
      onSuccess: () => {
        toast.success(`Unsubscribed ${p.email}`)
        setPending(null)
      },
      onError: (err) =>
        toast.danger(`Remove failed${err instanceof Error ? `: ${err.message}` : ''}`),
    })
  }

  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-primary">Sample Request Notifications</h2>
          <p className="text-sm text-secondary">
            Email recipients for Uviva sample-request events. Ella, Angie, Emma and Chris
            should be subscribed to “New Request Created”. Sends via Microsoft Graph; failures
            don&apos;t block the request.
          </p>
        </div>
        <Button variant="primary" onClick={() => setAddOpen(true)}>
          <Plus size={12} /> Add subscriber
        </Button>
      </header>

      {isLoading ? (
        <div className="py-6 text-center text-sm text-tertiary">Loading…</div>
      ) : (
        events.map((event) => {
          const rows = grouped.get(event) ?? []
          return (
            <section key={event} className="flex flex-col gap-2">
              <header className="flex items-baseline gap-2">
                <h3 className="text-sm font-semibold text-primary">
                  {EVENT_LABEL[event] ?? event}
                </h3>
                <code className="text-role-metadata">{event}</code>
                <span className="ml-auto text-role-metadata">
                  {rows.length} subscriber{rows.length === 1 ? '' : 's'}
                </span>
              </header>
              {rows.length === 0 ? (
                <EmptyState
                  variant="no-data"
                  title="No subscribers"
                  description={`Add an email to get notified on "${EVENT_LABEL[event] ?? event}".`}
                />
              ) : (
                <div className="overflow-hidden rounded-sm border border-border-subtle bg-surface">
                  <ul className="flex flex-col divide-y divide-border-subtle">
                    {rows.map((s) => (
                      <li key={s.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                        <span className="flex-1 text-primary">{s.email}</span>
                        {!s.active ? <span className="text-role-metadata">inactive</span> : null}
                        <Button
                          variant="ghost"
                          size="sm"
                          iconOnly
                          aria-label={`Remove ${s.email}`}
                          onClick={() => setPending(s)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )
        })
      )}

      {pending ? (
        <Modal
          open={pending !== null}
          onOpenChange={(open) => !open && setPending(null)}
          tone="danger"
          title="Remove subscriber"
          description={
            <>
              Remove <code>{pending.email}</code> from <code>{pending.event}</code>?
            </>
          }
          footer={
            <>
              <Button variant="ghost" onClick={() => setPending(null)}>
                Cancel
              </Button>
              <Button variant="danger" loading={remove.isPending} onClick={confirmRemove}>
                Remove
              </Button>
            </>
          }
        />
      ) : null}

      <AddSubscriberModal
        open={addOpen}
        events={events}
        onClose={() => setAddOpen(false)}
        onSubmit={(email, event) =>
          add.mutate(
            { email, event },
            {
              onSuccess: () => {
                toast.success(`Subscribed ${email}`)
                setAddOpen(false)
              },
              onError: (err) =>
                toast.danger(`Add failed${err instanceof Error ? `: ${err.message}` : ''}`),
            },
          )
        }
        submitting={add.isPending}
      />
    </section>
  )
}

function AddSubscriberModal({
  open,
  events,
  onClose,
  onSubmit,
  submitting,
}: {
  open: boolean
  events: SampleRequestEvent[]
  onClose: () => void
  onSubmit: (email: string, event: SampleRequestEvent) => void
  submitting: boolean
}) {
  const [email, setEmail] = useState('')
  const [event, setEvent] = useState<SampleRequestEvent>(events[0] ?? 'sample_request_created')
  function reset() {
    setEmail('')
    setEvent(events[0] ?? 'sample_request_created')
  }
  if (!open) return null
  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset()
          onClose()
        }
      }}
      tone="info"
      title="Add subscriber"
      description="Notifications fire via Microsoft Graph on every matching sample-request event."
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              reset()
              onClose()
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            disabled={!email.trim()}
            onClick={() => onSubmit(email.trim(), event)}
          >
            Add
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@siliconesplus.com"
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
            autoFocus
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Event</span>
          <Select value={event} onChange={(e) => setEvent(e.target.value as SampleRequestEvent)}>
            {events.map((ev) => (
              <option key={ev} value={ev}>
                {EVENT_LABEL[ev] ?? ev}
              </option>
            ))}
          </Select>
        </label>
      </div>
    </Modal>
  )
}
